/**
 * The text field that sits over a text box or a sticky note while it is being typed
 * into (SRS C4).
 *
 * Canvas cannot be typed into: it has no caret, no selection, no undo of its own and
 * no way to reach a phone's keyboard. So the words are typed into a real field placed
 * exactly over the box, in the same face, size, weight and colour they will be drawn
 * in — and the box itself is left unpainted underneath, so nothing shows twice.
 *
 * Everything here is positioned from the board's own coordinates, so the field stays
 * on its box while the board is panned and zoomed underneath it.
 */

import { useEffect, type CSSProperties } from 'react';

import { canvasToScreen, type BoardObject } from '@opal/shared';

import { fontFamily } from '../../board/text-paint.js';
import { LINE_HEIGHT_RATIO } from '../../board/text-layout.js';
import { useBoardStore } from '../../board/store.js';
import { stickySwatch } from '../../board/sticky-colors.js';
import {
  contentOf,
  fontSizeOf,
  paddingOf,
  stickyDataOf,
  textDataOf,
} from '../../board/text-object.js';

/** Where the field sits and how it is written in, all from the board's own numbers. */
function styleFor(object: BoardObject, zoom: number, style: CSSProperties): CSSProperties {
  const sticky = object.kind === 'sticky';
  const text = textDataOf(object);
  const size = fontSizeOf(object) * zoom;
  return {
    ...style,
    width: object.width * zoom,
    height: object.height * zoom,
    padding: paddingOf(object) * zoom,
    fontFamily: fontFamily(),
    fontSize: size,
    lineHeight: `${LINE_HEIGHT_RATIO}`,
    fontWeight: sticky ? 400 : text.fontWeight,
    fontStyle: !sticky && text.italic ? 'italic' : 'normal',
    textAlign: sticky ? 'left' : text.align,
    color: sticky ? 'var(--sticky-ink)' : text.color,
    background: sticky ? stickySwatch(stickyDataOf(object).color) : 'transparent',
  };
}

/**
 * Puts the caret back in the words being typed.
 *
 * The size slider has to take the focus to be dragged at all, which leaves the
 * teacher with no caret in the middle of their sentence. Letting go of it gives the
 * caret back, so writing carries on where it left off.
 */
export function focusWritingField(): void {
  const field = document.querySelector('[data-writing-field]');
  if (!(field instanceof HTMLTextAreaElement)) return;
  field.focus();
  field.setSelectionRange(field.value.length, field.value.length);
}

/**
 * Puts the caret in the box the moment it opens: a teacher who has just tapped where
 * they want to write should be able to write.
 *
 * Taken a frame later, not straight away. The tap that opened the box is still being
 * handled, and when the browser has finished with it it moves the focus to the page
 * itself — so a caret taken now is handed straight back, and the box would close the
 * instant it opened.
 */
function useCaret(openId: string | null): void {
  useEffect(() => {
    if (openId === null) return undefined;
    const frame = requestAnimationFrame(focusWritingField);
    return () => {
      cancelAnimationFrame(frame);
    };
  }, [openId]);
}

export function TextEditor() {
  const editing = useBoardStore((state) => state.editing);
  const writing = useBoardStore((state) => state.writing);
  const viewport = useBoardStore((state) => state.viewport);
  const openId = editing?.id ?? null;

  useCaret(openId);

  if (!editing || !writing) return null;

  const centre = canvasToScreen(viewport, {
    x: editing.x + editing.width / 2,
    y: editing.y + editing.height / 2,
  });

  return (
    <textarea
      data-writing-field
      data-board-ui
      aria-label={editing.kind === 'sticky' ? 'Sticky note' : 'Text'}
      value={contentOf(editing)}
      spellCheck={false}
      onChange={(event) => {
        writing.update(event.target.value);
      }}
      // Tapping inside the field is typing, not another tap on the board underneath.
      onPointerDown={(event) => {
        event.stopPropagation();
      }}
      onKeyDown={(event) => {
        // Escape finishes the box. Enter is a new line, as it is in any other field.
        if (event.key !== 'Escape') return;
        event.preventDefault();
        writing.commit();
      }}
      onBlur={(event) => {
        // Pressing Bold or dragging the size slider moves the focus out of the field.
        // That is not the teacher finishing: the box stays open and changes under
        // their hand. Anywhere else — the board, the header — finishes it.
        if (event.relatedTarget?.closest('[data-keeps-writing]')) return;
        writing.commit();
      }}
      className="absolute resize-none overflow-hidden rounded-sm outline outline-1 outline-primary"
      style={styleFor(editing, viewport.zoom, {
        left: centre.x - (editing.width * viewport.zoom) / 2,
        top: centre.y - (editing.height * viewport.zoom) / 2,
        transform: editing.rotation === 0 ? undefined : `rotate(${editing.rotation}rad)`,
        // Above every canvas layer, so the caret is never behind the ink.
        zIndex: 50,
      })}
    />
  );
}
